import React from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import './MagicElementsPage.scss';

interface MagicElementsPageProps {
}

const MagicElementsPage = (props: MagicElementsPageProps) => {

    const page: PageActions = new PageActions({
        title: `Casarok: Magic Elements - Chase the Fox`,
        description: "Learn about the various elements of Casarok's magic system!",
        keywords: ["casarok", "magic", "magic compendium", "magic system", "element system", "magical elements", "magic elements"],
        imgSrc: `chase/world/magic/elements/preview.png`,
        url: `world/magic/elements`
    });

    return page.generateMetaContent(
        <>
            <ContentBox header="Magical Elements">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <p>
                        Magic is divided
                        into <NavLink className="world-link" to="/world/magic/schools">four schools</NavLink> and 12
                        elements. It is often too much for most anthros to take in altogether, so most spellcasters
                        tend to focus on one element or spread their knowledge over a single school or group of
                        elements. Young magic users have historically been taught the chart below to learn all the
                        elements and their interactions from an early age.
                    </p>
                    <div className="elements-chart">
                        <div id="arcane-school">
                            <div id="divine-school">
                                <span>Divine</span>
                            </div>
                            <span>Arcane</span>
                            <img className="element-chart-image element-top-left"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/fire.png')}
                                 title="Fire"
                                 alt="Fire"/>
                            <img className="element-chart-image element-top-right"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/frost.png')}
                                 title="Frost"
                                 alt="Frost"/>
                            <img className="element-chart-image element-bottom-left"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/lightning.png')}
                                 title="Lightning"
                                 alt="Lightning"/>
                            <img className="element-chart-image element-bottom-right"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/space.png')}
                                 title="Space"
                                 alt="Space"/>
                        </div>
                        <div id="elemental-school">
                            <span>Elemental</span>
                            <img className="element-chart-image element-top-left"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/wind.png')}
                                 title="Wind"
                                 alt="Wind"/>
                            <img className="element-chart-image element-top-right"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/water.png')}
                                 title="Water"
                                 alt="Water"/>
                            <img className="element-chart-image element-bottom-left"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/time.png')}
                                 title="Time"
                                 alt="Time"/>
                            <img className="element-chart-image element-bottom-right"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/earth.png')}
                                 title="Earth"
                                 alt="Earth"/>
                        </div>
                        <div id="metaphysical-school">
                            <span>Metaphysical</span>
                            <img className="element-chart-image element-top-left"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/light.png')}
                                 title="Light"
                                 alt="Light"/>
                            <img className="element-chart-image element-top-right"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/dark.png')}
                                 title="Dark"
                                 alt="Dark"/>
                            <img className="element-chart-image element-bottom-left"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/nature.png')}
                                 title="Nature"
                                 alt="Nature"/>
                            <img className="element-chart-image element-bottom-right"
                                 src={_image_service.getCdnImageSrc('chase/world/magic/elements/spirit.png')}
                                 title="Spirit"
                                 alt="Spirit"/>
                        </div>
                    </div>
                    <h3 className="headline">Individual Element Explanations</h3>
                    <ol className="spaced">
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/fire">Fire</NavLink> -
                            Command over flame and heat by using one's mana to generate friction with the environment
                            around them
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/frost">Frost</NavLink> -
                            Command over ice and cold, using one's mana to draw heat from the environment around them
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/lightning">Lightning</NavLink> -
                            Command over electricity through manipulating positive and negative charges
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/wind">Wind</NavLink> -
                            Command of the air, wind, and gasses by observing their composition and using that info
                            to shape and move them
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/water">Water</NavLink> -
                            Command of water and waves by observing its composition to shape and manipulate it
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/earth">Earth</NavLink> -
                            Command of the stones and soil by determining their composition in order to shape and move
                            them
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/nature">Nature</NavLink> -
                            Command in the realm of plants, ferals, monsters, floradons, and other natural forces by
                            perceiving and learning how to harness their energy and manipulate those forces or themself
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/spirit">Spirit</NavLink> -
                            Command in the realm of living and deceased spirits by sensing their presence and energy,
                            utilizing one's spiritual energy and mana to cast spells
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/space">Space</NavLink> -
                            Command of spatial relations by using one's mana to shape and move matter or manipulate
                            its position in space; gravitational and void energy is encapsulated here as well
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/time">Time</NavLink> -
                            Command of temporal relations through the use of one's mana to shape and move another
                            entity through time; time stream manipulation also falls under this category
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/light">Light</NavLink> -
                            Command of the power of light and holy energy, utilizing one's mana and sense of light
                            energy to generate, shape, and move it
                        </li>
                        <li>
                            <NavLink className="world-link" to="/world/magic/elements/dark">Dark</NavLink> -
                            Command of the power of darkness and shadow energy, through the use of one's mana and
                            ability to sense darkness around them to generate, shape, and move such energy
                        </li>
                    </ol>
                </div>
            </ContentBox>
        </>
    );
}

export default MagicElementsPage;